import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useUIStore } from '@/stores/ui-store';

export function PreferencesPage() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'system');
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const [notifications, setNotifications] = useState(() => localStorage.getItem('notifications') !== 'false');
  const [platformKey, setPlatformKey] = useState(() => localStorage.getItem('kaycoo_platform_api_key') || '');

  const handleSave = () => {
    localStorage.setItem('theme', theme);
    localStorage.setItem('language', language);
    localStorage.setItem('notifications', String(notifications));
    if (platformKey) {
      localStorage.setItem('kaycoo_platform_api_key', platformKey);
    } else {
      localStorage.removeItem('kaycoo_platform_api_key');
    }

    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  };

  return (
    <div>
      <PageHeader title="Preferences" description="Customize your workspace" />

      <div className="max-w-2xl space-y-6">
        {/* Appearance */}
        <Card>
          <CardHeader><CardTitle className="text-base">Appearance</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Theme</Label>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Compact Sidebar</Label>
              <Switch checked={sidebarCollapsed} onCheckedChange={toggleSidebar} />
            </div>
          </CardContent>
        </Card>

        {/* Locale */}
        <Card>
          <CardHeader><CardTitle className="text-base">Language & Region</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fi">Suomi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader><CardTitle className="text-base">Notifications</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <Label>Enable Notifications</Label>
              <Switch checked={notifications} onCheckedChange={setNotifications} />
            </div>
          </CardContent>
        </Card>

        {/* Platform Connection */}
        <Card>
          <CardHeader><CardTitle className="text-base">Kaycoo Agentic Platform</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input
                type="password"
                value={platformKey}
                onChange={(e) => setPlatformKey(e.target.value)}
                placeholder="kap_..."
              />
              <p className="text-xs text-muted-foreground">
                Connect to the shared Kaycoo AI orchestration platform
              </p>
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} className="w-full">Save Preferences</Button>
      </div>
    </div>
  );
}
